import React from 'react';
import { useTranslation } from 'react-i18next';

import LockLine from '@/assets/icons/lock_line.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import TextField from '@/components/atoms/TextField';

import { PublicVideoLayout } from '../PublicVideoLayout';

import styles from '../public.module.scss';

interface ResetPasswordProps {
    resetPassword: (props: { newPassword: string }) => Promise<unknown>;
    hasToken: boolean;
    onSuccessfulReset: () => void;
}

export function ResetPassword({ resetPassword, hasToken, onSuccessfulReset }: ResetPasswordProps) {
    const [newPassword, setNewPassword] = React.useState('');
    const [error, setError] = React.useState<React.ReactNode>(null);
    const { t } = useTranslation();
    const onSubmit = async () => {
        try {
            await resetPassword({ newPassword });
            onSuccessfulReset();
        } catch (e: unknown) {
            console.log(e);
            setError(apiErrorContent(e));
        }
    };
    return (
        <PublicVideoLayout>
            <h2>{t('publicPages.common.resetPassword')}</h2>
            {hasToken ? (
                <>
                    <FormField
                        label={t('publicPages.resetPassword.newPassword')}
                        labelClassName={styles.inverseLabel}>
                        <TextField
                            prefix={<SvgIcon icon={LockLine} />}
                            groupClassName={styles.inputGroup}
                            adornmentClassName={styles.inputGroupText}
                            type="password"
                            name="password"
                            data-testid="password"
                            placeholder={t('publicPages.common.password')}
                            className={styles.formControl}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </FormField>

                    {error ? <Alert tone="danger">{error}</Alert> : ''}
                    <div className={styles.actions}>
                        <Button
                            variant="primary"
                            onClick={onSubmit}
                            data-testid="submitButton"
                            fullWidth
                            disabled={newPassword === ''}>
                            {t('publicPages.resetPassword.saveNewPassword')}
                        </Button>
                    </div>
                </>
            ) : (
                <Alert tone="danger">{t('publicPages.resetPassword.invalidToken')}</Alert>
            )}
        </PublicVideoLayout>
    );
}
